import type { EntryState, TimelineItem } from '../types';

export const CONTENT_DIMENSION_KEY = '_content';

export type CreateEntry = { text: string; state: EntryState; source: 'text' | 'voice'; sourceId?: number; userId?: number };
export type SourceRecord = { id: number; kind: 'document' | 'audio'; name: string; mimeType?: string | null; uri?: string | null; userId?: number };
export type WorkNodeRecord = { id: number; entryId?: number | null; sourceId?: number | null; userId?: number; kind: 'note' | 'event' | 'question'; text: string; ordinal?: number; weight: number };
export type SummaryRecord = { id: number; sourceId: number; userId?: number; text: string; level: number; ordinal: number; startOrdinal: number; endOrdinal: number };
export type ExtractionRecord = { id: number; nodeId: number; dimensionKey: string; startChar: number; endChar: number };
export type TeacherAnnotationInput = {
  entryId: number;
  userId: number;
  sourceText: string;
  model: 'gpt-5.4' | 'gpt-5.4-mini';
  promptKey: string;
  promptVersion: string;
  schemaVersion: string;
  tokenizerVersion: string;
  inputHash: string;
  payload: Record<string, unknown>;
};
export type EnrichmentPayload = {
  entryId: number | null;
  entryText: string;
  userId?: number;
  document?: { sourceId: number; userId: number; notes: Array<{ text: string; ordinal: number; weight: number }>; needsSummary: boolean };
  label: string;
};
export type EnrichmentJob = { id: number; payload: EnrichmentPayload; attempts: number; nextAttemptAt: string; lastError: string | null };
export type EnrichmentTaskResult = { completed: boolean; result?: unknown };
export type InterruptedDocument = { sourceId: number; name: string; mimeType: string | null; userId: number; content: Uint8Array; entryId: number | null; entryText: string };
export type SemanticDimension = { id: number; key: string; name: string; prompt: string; enabled: boolean };
export type SemanticPrompt = { id: number; key: string; purpose: string; template: string; modelHint: string | null };
export type ClusterLeafKind = 'source' | 'entry' | 'node' | 'summary' | 'extraction';
export type EmbeddingOwnerKind = ClusterLeafKind | 'cluster';
export type ClusterInput = { id: number; ownerKind: ClusterLeafKind; dimensionKey: string; text: string; vector: number[]; userId?: number; weight?: number };
export type ClusterVectorCache = { dimensionKey: string; level: number; text: string; vector: number[] };
export type ClusterDraft = { clientKey: string; dimensionKey: string; level: number; text: string; vector: number[]; userId?: number; members: Array<{ kind: ClusterLeafKind; id: number } | { kind: 'cluster'; clientKey: string }> };

export interface DataPort {
  readonly location: 'local' | 'remote';
  listTimeline(userKey: string): Promise<TimelineItem[]>;
  createEntry(input: CreateEntry): Promise<TimelineItem>;
  updateState(id: number, state: EntryState): Promise<void>;
  ensureUser(externalKey: string, name: string): Promise<number>;
  createSource(input: Omit<SourceRecord, 'id'> & { content?: Uint8Array }): Promise<SourceRecord>;
  createNode(input: Omit<WorkNodeRecord, 'id'>): Promise<WorkNodeRecord>;
  createNodes?(inputs: Array<Omit<WorkNodeRecord, 'id'>>): Promise<WorkNodeRecord[]>;
  createSummary(input: Omit<SummaryRecord, 'id'>): Promise<SummaryRecord>;
  createExtraction(input: Omit<ExtractionRecord, 'id'>): Promise<ExtractionRecord>;
  createEdge(fromExtractionId: number, toExtractionId: number, relation: string): Promise<void>;
  saveTeacherAnnotation(input: TeacherAnnotationInput): Promise<void>;
  putEmbedding(ownerKind: EmbeddingOwnerKind, ownerId: number, dimensionKey: string | null, vector: number[]): Promise<void>;
  listClusterInputs(userId?: number): Promise<ClusterInput[]>;
  listClusterVectorCache(userId?: number): Promise<ClusterVectorCache[]>;
  rebuildClusters(clusters: ClusterDraft[], userId?: number): Promise<void>;
  enqueueEnrichment?(payload: EnrichmentPayload): Promise<void>;
  listDueEnrichments?(at: string): Promise<EnrichmentJob[]>;
  completeEnrichment?(id: number): Promise<void>;
  retryEnrichment?(id: number, nextAttemptAt: string, error: string): Promise<void>;
  failEnrichment?(id: number, error: string): Promise<void>;
  getEnrichmentTask?(jobId: number, taskKey: string): Promise<EnrichmentTaskResult>;
  completeEnrichmentTask?(jobId: number, taskKey: string, result?: unknown): Promise<void>;
  runWriteBatch?<T>(task: () => Promise<T>): Promise<T>;
  listInterruptedDocuments?(): Promise<InterruptedDocument[]>;
  sync?(endpoint: string, accountKey: string): Promise<{ pushed: number; pulled: number; warnings?: string[] }>;
  clearAll?(): Promise<void>;
  resetSync?(accountKey: string): Promise<void>;
}
